/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1710460800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Mar 15 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "quickbooks",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to QuickBooks",
          "depth": 0,
          "id": "connect-to-quickbooks"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing accounts",
          "depth": 1,
          "id": "syncing-accounts"
        }, {
          "value": "Syncing bills",
          "depth": 1,
          "id": "syncing-bills"
        }, {
          "value": "Syncing classes",
          "depth": 1,
          "id": "syncing-classes"
        }, {
          "value": "Syncing customers",
          "depth": 1,
          "id": "syncing-customers"
        }, {
          "value": "Syncing deposits",
          "depth": 1,
          "id": "syncing-deposits"
        }, {
          "value": "Syncing invoices",
          "depth": 1,
          "id": "syncing-invoices"
        }, {
          "value": "Syncing journal entries",
          "depth": 1,
          "id": "syncing-journal-entries"
        }, {
          "value": "Syncing payments",
          "depth": 1,
          "id": "syncing-payments"
        }, {
          "value": "Syncing payment methods",
          "depth": 1,
          "id": "syncing-payment-methods"
        }, {
          "value": "Syncing RecurringTransaction objects",
          "depth": 1,
          "id": "syncing-recurringtransaction-objects"
        }, {
          "value": "Syncing TimeActivity objects",
          "depth": 1,
          "id": "syncing-timeactivity-objects"
        }, {
          "value": "Syncing vendors",
          "depth": 1,
          "id": "syncing-vendors"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      code: "code",
      strong: "strong",
      a: "a",
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      h3: "h3",
      h4: "h4",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components), {PylonLink, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Hightouch uses minor version ", _jsx(_components.code, {
            children: "65"
          }), " of the ", _jsx(_components.strong, {
            children: "QuickBooks Online Accounting API"
          }), ".\nSee QuickBooks's\n", _jsx(_components.a, {
            href: "https://developer.intuit.com/app/developer/qbo/docs/learn/explore-the-quickbooks-online-api/minor-versions",
            children: "docs"
          }), "\nto learn more about minor versions."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Account"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to QuickBooks Accounts"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/account",
                children: "Account docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Bill"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to QuickBooks Bills"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/bill",
                children: "Bill docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Class"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to QuickBooks Classes"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/class",
                children: "Class docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Customer"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Quickbook Customers"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/customer",
                children: "Customer docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Deposit"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to QuickBooks Deposits"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/deposit",
                children: "Deposit docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Invoice"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to QuickBooks Invoices"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/invoice",
                children: "Invoice docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "JournalEntry"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to QuickBooks Journal Entries"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/journalentry",
                children: "JournalEntry docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Payment"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to QuickBooks Payments"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/payment",
                children: "Payment docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "PaymentMethod"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to QuickBooks Payment Methods"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/paymentmethod",
                children: "PaymentMethod docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "RecurringTransaction"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to QuickBooks RecurringTransaction objects"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/recurringtransaction",
                children: "RecurringTransaction docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "TimeActivits"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to QuickBooks TimeActivity objects"
            }), _jsx(_components.td, {
              children: "Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/timeactivity",
                children: "TimeActivity docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Vendor"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to QuickBooks Vendors"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/vendor",
                children: "Vendor docs"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-quickbooks",
        children: "Connect to QuickBooks"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "QuickBooks"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "QuickBooks"
        }), " via OAuth."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can choose to sync your data to a Sandbox or Production Quickbooks environment. Select your preference, click ", _jsx(_components.strong, {
          children: "Log in to QuickBooks"
        }), ", and log into your QuickBooks account. Once successful, you will be redirected back to Hightouch to enter a descriptive name for your destination and complete setup."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your QuickBooks destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the QuickBooks destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-accounts",
        children: "Syncing accounts"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to accounts in QuickBooks."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows from your model to accounts in Quickbooks on any column in your model and any account field in Quickbooks. Ensure the data types of the model column and Quickbooks field you select match."
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, QuickBooks automatically generates an identifier for every\nnew record synced, so there is no need to match an existing record."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can map data from any of your model columns to fields in QuickBooks."
      }), "\n", _jsxs(_components.p, {
        children: ["QuickBooks requires the ", _jsxs(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/account",
          children: [_jsx(_components.code, {
            children: "SyncToken"
          }), " attribute"]
        }), " when performing updates."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "SyncToken"
        }), " is the version number of the object. It's used to lock an object for use by one app at a time. As soon as an application modifies an object, its SyncToken is incremented."]
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch pulls the latest ", _jsx(_components.code, {
          children: "SyncToken"
        }), " from QuickBooks before perfoming the update. You can override this behavior by providing your own ", _jsx(_components.code, {
          children: "SyncToken"
        }), " value via field mapping. Keep in mind that attempts to modify an object specifying an older ", _jsx(_components.code, {
          children: "SyncToken"
        }), " fail."]
      }), "\n", _jsx(_components.p, {
        children: "QuickBooks also requires the following fields, so you must map them to complete your configuration:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Name"
          }), ": User recognizable name for the Account. 100 character max."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Ensure the data types of your model columns match the data types of the fields you want to sync to. Refer to QuickBooks ", _jsx(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/account",
          children: "account object docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-bills",
        children: "Syncing bills"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to bills in QuickBooks."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows from your model to bills in Quickbooks on any column in your model and any bill field in Quickbooks. Ensure the data types of the model column and Quickbooks field you select match."
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, QuickBooks automatically generates an identifier for every\nnew record synced, so there is no need to match an existing record."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can map data from any of your model columns to fields in QuickBooks."
      }), "\n", _jsxs(_components.p, {
        children: ["QuickBooks requires the ", _jsxs(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/customer",
          children: [_jsx(_components.code, {
            children: "SyncToken"
          }), " attribute"]
        }), " when performing updates."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "SyncToken"
        }), " is the version number of the object. It's used to lock an object for use by one app at a time. As soon as an application modifies an object, its SyncToken is incremented."]
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch pulls the latest ", _jsx(_components.code, {
          children: "SyncToken"
        }), " from QuickBooks before perfoming the update. You can override this behavior by providing your own ", _jsx(_components.code, {
          children: "SyncToken"
        }), " value via field mapping. Keep in mind that attempts to modify an object specifying an older ", _jsx(_components.code, {
          children: "SyncToken"
        }), " fail."]
      }), "\n", _jsx(_components.p, {
        children: "QuickBooks also requires the following fields, so you must map them to complete your configuration:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "VendorRef"
          }), ": Reference to the vendor for this transaction. Query the Vendor name list resource to determine the appropriate Vendor object for this reference. Use ", _jsx(_components.code, {
            children: "Vendor.Id"
          }), " and ", _jsx(_components.code, {
            children: "Vendor.Name"
          }), " from that object for ", _jsx(_components.code, {
            children: "VendorRef.value"
          }), " and ", _jsx(_components.code, {
            children: "VendorRef.name"
          }), ", respectively."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Line"
          }), ": Individual line items of a transaction. Valid Line types include: ", _jsx(_components.code, {
            children: "ItemBasedExpenseLine"
          }), " and ", _jsx(_components.code, {
            children: "AccountBasedExpenseLine"
          })]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Ensure the data types of your model columns match the data types of the fields you want to sync to. Refer to QuickBooks ", _jsx(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/bill",
          children: "bill object docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-classes",
        children: "Syncing classes"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to classes in QuickBooks."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-2",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows from your model to classes in Quickbooks on any column in your model and any class field in Quickbooks. Ensure the data types of the model column and Quickbooks field you select match."
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, QuickBooks automatically generates an identifier for every\nnew record synced, so there is no need to match an existing record."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-2",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can map data from any of your model columns to fields in QuickBooks."
      }), "\n", _jsxs(_components.p, {
        children: ["QuickBooks requires the ", _jsxs(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/class",
          children: [_jsx(_components.code, {
            children: "SyncToken"
          }), " attribute"]
        }), " when performing updates."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "SyncToken"
        }), " is the version number of the object. It's used to lock an object for use by one app at a time. As soon as an application modifies an object, its SyncToken is incremented."]
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch pulls the latest ", _jsx(_components.code, {
          children: "SyncToken"
        }), " from QuickBooks before perfoming the update. You can override this behavior by providing your own ", _jsx(_components.code, {
          children: "SyncToken"
        }), " value via field mapping. Keep in mind that attempts to modify an object specifying an older ", _jsx(_components.code, {
          children: "SyncToken"
        }), " fail."]
      }), "\n", _jsx(_components.p, {
        children: "QuickBooks also requires the following fields, so you must map them to complete your configuration:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Name"
          }), ": User recognizable name for the Class. 100 character max."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Ensure the data types of your model columns match the data types of the fields you want to sync to. Refer to QuickBooks ", _jsx(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/class",
          children: "class object docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-customers",
        children: "Syncing customers"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to customers in QuickBooks."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-3",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows from your model to customers in Quickbooks on any column in your model and any customer field in Quickbooks. Ensure the data types of the model column and Quickbooks field you select match."
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, QuickBooks automatically generates an identifier for every\nnew record synced, so there is no need to match an existing record."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-3",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your model to QuickBooks default and custom fields."
      }), "\n", _jsxs(_components.p, {
        children: ["QuickBooks requires the ", _jsxs(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/customer",
          children: [_jsx(_components.code, {
            children: "SyncToken"
          }), " attribute"]
        }), " when performing updates."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "SyncToken"
        }), " is the version number of the object. It's used to lock an object for use by one app at a time. As soon as an application modifies an object, its SyncToken is incremented."]
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch pulls the latest ", _jsx(_components.code, {
          children: "SyncToken"
        }), " from QuickBooks before perfoming the update. You can override this behavior by providing your own ", _jsx(_components.code, {
          children: "SyncToken"
        }), " value via field mapping. Keep in mind that attempts to modify an object specifying an older ", _jsx(_components.code, {
          children: "SyncToken"
        }), " fail."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-deposits",
        children: "Syncing deposits"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to deposits in QuickBooks."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-4",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows from your model to deposits in Quickbooks on any column in your model and any deposit field in Quickbooks. Ensure the data types of the model column and Quickbooks field you select match."
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, QuickBooks automatically generates an identifier for every\nnew record synced, so there is no need to match an existing record."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-4",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can map data from any of your model columns to fields in QuickBooks."
      }), "\n", _jsxs(_components.p, {
        children: ["QuickBooks requires the ", _jsxs(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/deposit",
          children: [_jsx(_components.code, {
            children: "SyncToken"
          }), " attribute"]
        }), " when performing updates."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "SyncToken"
        }), " is the version number of the object. It's used to lock an object for use by one app at a time. As soon as an application modifies an object, its SyncToken is incremented."]
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch pulls the latest ", _jsx(_components.code, {
          children: "SyncToken"
        }), " from QuickBooks before perfoming the update. You can override this behavior by providing your own ", _jsx(_components.code, {
          children: "SyncToken"
        }), " value via field mapping. Keep in mind that attempts to modify an object specifying an older ", _jsx(_components.code, {
          children: "SyncToken"
        }), " fail."]
      }), "\n", _jsx(_components.p, {
        children: "QuickBooks also requires the following fields, so you must map them to complete your configuration:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "DepositToAccountRef"
          }), ": Identifies the account to be used for this deposit."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Line"
          }), ": Atleast 1 line item must be included to create a deposit."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Ensure the data types of your model columns match the data types of the fields you want to sync to. Refer to QuickBooks ", _jsx(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/deposit",
          children: "deposit object docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-invoices",
        children: "Syncing invoices"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to invoices in QuickBooks."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-5",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows from your model to invoices in Quickbooks on any column in your model and any invoice field in Quickbooks. Ensure the data types of the model column and Quickbooks field you select match."
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, QuickBooks automatically generates an identifier for every\nnew record synced, so there is no need to match an existing record."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-5",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can map data from any of your model columns to fields in QuickBooks."
      }), "\n", _jsxs(_components.p, {
        children: ["QuickBooks requires the ", _jsxs(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/invoice",
          children: [_jsx(_components.code, {
            children: "SyncToken"
          }), " attribute"]
        }), " when performing updates."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "SyncToken"
        }), " is the version number of the object. It's used to lock an object for use by one app at a time. As soon as an application modifies an object, its SyncToken is incremented."]
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch pulls the latest ", _jsx(_components.code, {
          children: "SyncToken"
        }), " from QuickBooks before perfoming the update. You can override this behavior by providing your own ", _jsx(_components.code, {
          children: "SyncToken"
        }), " value via field mapping. Keep in mind that attempts to modify an object specifying an older ", _jsx(_components.code, {
          children: "SyncToken"
        }), " fail."]
      }), "\n", _jsx(_components.p, {
        children: "QuickBooks also requires the following fields, so you must map them to complete your configuration:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "DepositToAccountRef"
          }), ": Identifies the account to be used for this deposit."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Line"
          }), ": Atleast 1 line item must be included to create a deposit."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Ensure the data types of your model columns match the data types of the fields you want to sync to. Refer to QuickBooks ", _jsx(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/invoice",
          children: "invoice object docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-journal-entries",
        children: "Syncing journal entries"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to journal entries in QuickBooks."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-6",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows from your model to jounal entries in Quickbooks on any column in your model and any journal entry field in Quickbooks. Ensure the data types of the model column and Quickbooks field you select match."
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, QuickBooks automatically generates an identifier for every\nnew record synced, so there is no need to match an existing record."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-6",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can map data from any of your model columns to fields in QuickBooks."
      }), "\n", _jsxs(_components.p, {
        children: ["QuickBooks requires the ", _jsxs(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/journalentry",
          children: [_jsx(_components.code, {
            children: "SyncToken"
          }), " attribute"]
        }), " when performing updates."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "SyncToken"
        }), " is the version number of the object. It's used to lock an object for use by one app at a time. As soon as an application modifies an object, its SyncToken is incremented."]
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch pulls the latest ", _jsx(_components.code, {
          children: "SyncToken"
        }), " from QuickBooks before perfoming the update. You can override this behavior by providing your own ", _jsx(_components.code, {
          children: "SyncToken"
        }), " value via field mapping. Keep in mind that attempts to modify an object specifying an older ", _jsx(_components.code, {
          children: "SyncToken"
        }), " fail."]
      }), "\n", _jsx(_components.p, {
        children: "QuickBooks also requires the following fields, so you must map them to complete your configuration:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Line"
          }), ": Individual line items of a transaction. There must be at least one pair of Journal Entry Line elements, representing a debit and a credit, called distribution lines."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Ensure the data types of your model columns match the data types of the fields you want to sync to. Refer to QuickBooks ", _jsx(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/journalentry",
          children: "journal entry object docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-payments",
        children: "Syncing payments"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to payments in QuickBooks."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-7",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows from your model to payments in Quickbooks on any column in your model and any payment field in Quickbooks. Ensure the data types of the model column and Quickbooks field you select match."
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, QuickBooks automatically generates an identifier for every\nnew record synced, so there is no need to match an existing record."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-7",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can map data from any of your model columns to fields in QuickBooks."
      }), "\n", _jsxs(_components.p, {
        children: ["QuickBooks requires the ", _jsxs(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/payment",
          children: [_jsx(_components.code, {
            children: "SyncToken"
          }), " attribute"]
        }), " when performing updates."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "SyncToken"
        }), " is the version number of the object. It's used to lock an object for use by one app at a time. As soon as an application modifies an object, its SyncToken is incremented."]
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch pulls the latest ", _jsx(_components.code, {
          children: "SyncToken"
        }), " from QuickBooks before perfoming the update. You can override this behavior by providing your own ", _jsx(_components.code, {
          children: "SyncToken"
        }), " value via field mapping. Keep in mind that attempts to modify an object specifying an older ", _jsx(_components.code, {
          children: "SyncToken"
        }), " fail."]
      }), "\n", _jsx(_components.p, {
        children: "QuickBooks also requires the following fields, so you must map them to complete your configuration:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "TotalAmt"
          }), ": Indicates the total amount of the transaction. This includes the total of all the charges, allowances, and taxes."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "CustomerRef"
          }), ": Reference to a customer or job."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Ensure the data types of your model columns match the data types of the fields you want to sync to. Refer to QuickBooks ", _jsx(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/payment",
          children: "payment object docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-payment-methods",
        children: "Syncing payment methods"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to payment methods in QuickBooks."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-8",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows from your model to payment methods in Quickbooks on any column in your model and any payment method field in Quickbooks. Ensure the data types of the model column and Quickbooks field you select match."
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, QuickBooks automatically generates an identifier for every\nnew record synced, so there is no need to match an existing record."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-8",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can map data from any of your model columns to fields in QuickBooks."
      }), "\n", _jsxs(_components.p, {
        children: ["QuickBooks requires the ", _jsxs(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/paymentmethod",
          children: [_jsx(_components.code, {
            children: "SyncToken"
          }), " attribute"]
        }), " when performing updates."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "SyncToken"
        }), " is the version number of the object. It's used to lock an object for use by one app at a time. As soon as an application modifies an object, its SyncToken is incremented."]
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch pulls the latest ", _jsx(_components.code, {
          children: "SyncToken"
        }), " from QuickBooks before perfoming the update. You can override this behavior by providing your own ", _jsx(_components.code, {
          children: "SyncToken"
        }), " value via field mapping. Keep in mind that attempts to modify an object specifying an older ", _jsx(_components.code, {
          children: "SyncToken"
        }), " fail."]
      }), "\n", _jsx(_components.p, {
        children: "QuickBooks also requires the following fields, so you must map them to complete your configuration:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Name"
          }), ": User recognizable name for the payment method. 31 character max."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Ensure the data types of your model columns match the data types of the fields you want to sync to. Refer to QuickBooks ", _jsx(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/paymentmethod",
          children: "payment method object docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-recurringtransaction-objects",
        children: "Syncing RecurringTransaction objects"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports syncing RecurringTransaction objects to the ", _jsx(_components.strong, {
          children: "Bill"
        }), " entity. This sets the ", _jsx(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/recurringtransaction",
          children: _jsx(_components.code, {
            children: "type"
          })
        }), " field on the ecurringTransaction objects."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you're interested in syncing RecurringTransations to another entity, please ", _jsx(PylonLink, {
          pylonText: "Hello, I have a feature request. I'd like to sync QuickBooks RecurringTransations with the {...} entity.",
          linkText: "let us know"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-9",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Since you can only insert RecurringTransaction objects, QuickBooks automatically generates an identifier for every new record synced, so there is no need to match an existing record."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-9",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can map data from any of your model columns to fields in QuickBooks."
      }), "\n", _jsxs(_components.p, {
        children: ["QuickBooks requires the ", _jsxs(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/customer",
          children: [_jsx(_components.code, {
            children: "SyncToken"
          }), " attribute"]
        }), " when performing updates."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "SyncToken"
        }), " is the version number of the object. It's used to lock an object for use by one app at a time. As soon as an application modifies an object, its SyncToken is incremented."]
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch pulls the latest ", _jsx(_components.code, {
          children: "SyncToken"
        }), " from QuickBooks before perfoming the update. You can override this behavior by providing your own ", _jsx(_components.code, {
          children: "SyncToken"
        }), " value via field mapping. Keep in mind that attempts to modify an object specifying an older ", _jsx(_components.code, {
          children: "SyncToken"
        }), " fail."]
      }), "\n", _jsxs(_components.p, {
        children: ["QuickBooks also requires the ", _jsx(_components.strong, {
          children: "RecurringInfo"
        }), " field, so you must map it to complete your configuration. Ensure the data types of your model columns match the data types of the fields you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-timeactivity-objects",
        children: "Syncing TimeActivity objects"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to TimeActivity objects in QuickBooks."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-10",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows from your model to TimeActivity objects in Quickbooks on any column in your model and any TimeActivity field in Quickbooks. Ensure the data types of the model column and Quickbooks field you select match."
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, QuickBooks automatically generates an identifier for every\nnew record synced, so there is no need to match an existing record."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-10",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can map data from any of your model columns to fields in QuickBooks."
      }), "\n", _jsxs(_components.p, {
        children: ["QuickBooks requires the ", _jsxs(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/customer",
          children: [_jsx(_components.code, {
            children: "SyncToken"
          }), " attribute"]
        }), " when performing updates."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "SyncToken"
        }), " is the version number of the object. It's used to lock an object for use by one app at a time. As soon as an application modifies an object, its SyncToken is incremented."]
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch pulls the latest ", _jsx(_components.code, {
          children: "SyncToken"
        }), " from QuickBooks before perfoming the update. You can override this behavior by providing your own ", _jsx(_components.code, {
          children: "SyncToken"
        }), " value via field mapping. Keep in mind that attempts to modify an object specifying an older ", _jsx(_components.code, {
          children: "SyncToken"
        }), " fail."]
      }), "\n", _jsx(_components.p, {
        children: "QuickBooks also requires the following fields, so you must map them to complete your configuration:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "NameOf"
          }), " - Value can be ", _jsx(_components.code, {
            children: "Vendor"
          }), " or ", _jsx(_components.code, {
            children: "Employee"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "VendorRef"
          }), " - Required if ", _jsx(_components.strong, {
            children: "NameOf"
          }), " is set to ", _jsx(_components.code, {
            children: "Vendor"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "EmployeeRef"
          }), " - Required if ", _jsx(_components.strong, {
            children: "NameOf"
          }), " is set to ", _jsx(_components.code, {
            children: "Employee"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Ensure the data types of your model columns match the data types of the fields you want to sync to."
      }), "\n", _jsx(_components.h3, {
        id: "syncing-vendors",
        children: "Syncing vendors"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to vendors in QuickBooks."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-11",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows from your model to vendors in Quickbooks on any column in your model and any vendor field in Quickbooks. Ensure the data types of the model column and Quickbooks field you select match."
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, QuickBooks automatically generates an identifier for every\nnew record synced, so there is no need to match an existing record."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-11",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your model to QuickBooks default and custom fields."
      }), "\n", _jsxs(_components.p, {
        children: ["QuickBooks requires the ", _jsxs(_components.a, {
          href: "https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/customer",
          children: [_jsx(_components.code, {
            children: "SyncToken"
          }), " attribute"]
        }), " when performing updates."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "SyncToken"
        }), " is the version number of the object. It's used to lock an object for use by one app at a time. As soon as an application modifies an object, its SyncToken is incremented."]
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch pulls the latest ", _jsx(_components.code, {
          children: "SyncToken"
        }), " from QuickBooks before perfoming the update. You can override this behavior by providing your own ", _jsx(_components.code, {
          children: "SyncToken"
        }), " value via field mapping. Keep in mind that attempts to modify an object specifying an older ", _jsx(_components.code, {
          children: "SyncToken"
        }), " fail."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
